import * as Commons from "oc-commons/Commons";
import * as Doms from "oc-ui/Doms";
import * as Debugs from "oc-threejs/Debugs";
import { DefaultAppSettings, IDefaultAppSettings } from "./DefaultAppSettings";
import { DeviceDetection } from "oc-commons/DeviceDetection";
import * as Dialogs from "oc-ui/dialogs/Dialogs";
import { I18N } from "oc-I18N";
import { ListenerMixin } from "oc-commons/ListenerMixin";
import { localSettings } from "oc-ui/localSettings";
import LZString from "lz-string";
import * as Mimetypes from "oc-commons/Mimetypes";
import { UndoHandler } from "oc-app/UndoHandler";
import { Urls } from "oc-commons/Urls";
import { Wkfg2BackendApi } from "oc-app/Wkfg2BackendApi";
import { Wkfg2TypeConverter } from "oc-app/Wkfg2TypeConverter";
import Cookies from "js-cookie";
import { ModelsHelper } from "./ModelsHelper";
import { getMimetypeOfFilename } from "oc-commons/Mimetypes";

import * as _ from "lodash";
import handlebars from "handlebars";
import { LineSegments, Object3D } from "three";
// Assuming HBS compiles to handlebars call or similar global HBS?

// Assuming HBS is global

(self as any).Commons = Commons;
(self as any).Debugs = Debugs;

Debugs.checkBabelSetSpread();

export interface IAbstractAppSettings extends IDefaultAppSettings {
  i18n_json?: any;
  xcsrfToken: string;
  tenant_key: string;
  appKey: string;
  homeUrl: string;
  userRole: "admin" | "kunde_eingeloggt" | "kunde_nicht_eingeloggt";
  wkfgBackendUrl: string;
  configuratorUrl: string;
  pleaseWaitFacebookUrl: string;
  pleaseWaitPinterestUrl: string;
  wkfgContentBaseUrl: string;
  userId: number;
  userGuid: string;
  autosaveAndRestore: boolean;
  Code?: string;
}

export interface IConfiguration {
  id?: string | null;
  Code?: string | null;
  Json?: any;
  Inspiration?: boolean;
  Locked?: boolean;
  UserGuid?: string;
  UserId?: string;
  camera?: any;
  [key: string]: any;
}

export interface IRenderOptions {
  projectName?: string;
  filename?: string;
  object3d?: Object3D | null;
  code?: string | null;
  before?: ((o3: Object3D) => void) | null;
  after?: ((o3: Object3D) => void) | null;
  hideknown?: boolean;
  instancesToBeHidden?: any[];
  renderData?: any;
  multipart?: number;
  part?: number;
  useRenderfarm?: boolean;
  blendFilename?: string;
}

export interface IAROptions {
  projectName?: string | null;
  ARUvMapping?: boolean | null;
  convertToUSDZ?: boolean | null;
  object3d?: Object3D | null;
  code?: string | null;
  before?: ((o3: Object3D) => void) | null;
  after?: ((o3: Object3D) => void) | null;
  hideknown?: boolean;
  instancesToBeHidden?: any[];
}

export interface ISaveOptions {
  callback?: (configuration: any) => void;
  inSaveCallback?: (cb: () => void) => void;
  preferUpdate?: boolean;
  forceNoUpdate?: boolean;
  quiet?: boolean;
  noFotoSnapshots?: boolean;
  modifyBeforeWrite?: (configuration: any, method: string) => void;
  onCancel?: () => void;
  additionalParameter?: Record<string, any>;
  rendering?: boolean; // inferred from pdf logic if needed
}

export class AbstractApp extends ListenerMixin(Object) {
  name: string;
  container: HTMLElement;
  settingsHandler: any;
  settings: IAbstractAppSettings;
  undoHandler: UndoHandler;
  metadata: any = [];
  metamodel: any;
  configurationList: any[] = [];
  configuration: IConfiguration | null = null;
  printDesignerMap: any;
  user: any = null;
  ready: boolean = false;
  _wkfgBackendApi: Wkfg2BackendApi | undefined;
  _modelsHelper: ModelsHelper | undefined;
  representer: any;
  _configurationSnapshot: any;
  uploadSketchesReady: boolean = false;
  uploadSketchesTimeout: number = 0;
  uploadSketches?: () => { name: string; canvasPromise: Promise<HTMLCanvasElement> }[];
  version: string = "<<<<<---RELEASE_NUMBER--->>>>>";

  constructor(name: string, container: HTMLElement = document.body, settings: any = null) {
    super();
    this.name = name;
    this.container = container;
    this._appInitialize(settings);
  }

  _appInitialize(settings: any) {
    this._appInitializeFromAbstractApp(settings);
  }

  _appInitializeFromAbstractApp(settings: any) {
    this.version = "<<<<<---RELEASE_NUMBER--->>>>>";

    if (this.container == null) {
      throw new Error("App constructed without container element");
    }

    // Convenience. Könnte bei Namenskollisionen mit umgebender Seite mittels string-replace-webpack-plugin ersetzt werden.
    (self as any)["APP"] = this;

    if (settings && settings.i18n_json) {
      I18N.setKeyValueMap(settings.i18n_json);
    }

    this.mergeSettings(settings);

    this.on("ready", () => {
      this.ready = true;
      document.body.classList.add("holoAppReady");
    });

    this.checkWebGL();

    this.undoHandler = this.createUndoHandler();

    this.wkfgBackendApi = this.createWkfgBackendApi();
  }

  createUndoHandler() {
    return new UndoHandler();
  }

  getAjaxHeaders() {
    return {
      "X-CSRF-Token": APP.settings.xcsrfToken,
      lang: I18N.getLanguage(),
      "wkfg-tenant": APP.settings.tenant_key,
      wkfgapp: APP.settings.appKey,
    };
  }

  mergeSettings(settings: any) {
    this.settingsHandler = localSettings(this.name + "_settings", settings);
    this.settings = Commons.defaultsDeepSafe(
      {},
      this.settingsHandler.getMerge(),
      DefaultAppSettings,
    );
  }

  checkWebGL() {
    if (!DeviceDetection.isWebGLAvailable()) {
      this.noWebGL();
      return false;
    }
  }

  noWebGL() {
    alert(I18N("Auf Ihrem System steht leider kein WebGL zur Verfügung."));
  }

  setStageFromConfiguration(configurationJson: any = null) {}

  setCameraConfig(cameraConfig: any) {
    _.delay(() => {
      STAGE.setCameraConfig(cameraConfig);
    }, 500);
  }

  updateConfigurationWithUnchangedJson(configurationJson: any = null) {
    this.configuration = configurationJson;
  }

  getStageAsConfiguration(): any {
    return {};
  }

  getStageValidationErrors(): string[] {
    return [];
  }

  getCurrentConfiguration(): IConfiguration {
    return {};
  }

  maybeSetAutosaveAndRestore() {
    const params = new URLSearchParams(self.location.search);
    const autoSaveParam = params.get("autoSave");
    if (autoSaveParam) {
      this.settings.autosaveAndRestore = autoSaveParam == "true";
    }
    if (this.settings.autosaveAndRestore) {
      this.setAutosaveAndRestore();
    }
  }

  deleteAutosavedConfiguration() {
    localStorage.removeItem("configuration");
    localStorage.removeItem("configurationCamera");
  }

  setAutosaveAndRestore(eventName = "changedConfiguration") {
    let wasStoredInThisSesssion = false;
    const deleteStoredConfiguration = () => {
      // console.log('delete localStorage.configuration');
      this.deleteAutosavedConfiguration();
      wasStoredInThisSesssion = false;
    };
    const debouncedSave = _.debounce(() => {
      const configurationCamera = STAGE.getCurrentCameraConfig();
      if (configurationCamera) {
        localStorage["configurationCamera"] = JSON.stringify(configurationCamera);
      }
      if (APP.isConfigurationChanged()) {
        console.debug(
          "store localStorage.configuration because ",
          eventName,
          APP.getStageAsConfiguration(),
        );

        localStorage["configuration"] = LZString.compress(
          JSON.stringify(APP.getStageAsConfiguration()),
        );
        wasStoredInThisSesssion = true;
      } else if (wasStoredInThisSesssion) {
        deleteStoredConfiguration();
      }
    }, 1000);

    APP.on(eventName, debouncedSave);
    APP.on("savedConfiguration", deleteStoredConfiguration);
    const deregister = () => {
      APP.off(eventName, debouncedSave);
      APP.off("savedConfiguration", deleteStoredConfiguration);
    };

    const cameraConfig =
      localStorage["configurationCamera"] == null
        ? null
        : JSON.parse(localStorage["configurationCamera"]);
    if (localStorage["configuration"] != null) {
      try {
        const storedConfiguration = JSON.parse(LZString.decompress(localStorage["configuration"]));
        APP.onOnce("loadedInitialConfiguration", () => {
          if (!APP.settings.autosaveAndRestore) {
            deregister();
            return;
          }
          // hier warten wir noch mal 2 Sekunden, weil einige Apps wie Zaunplaner die Konfiguration nach dem Laden
          // noch mal aktualisieren und transiente Parameter hinzugefügt werden
          _.delay(() => {
            try {
              // hier prüfen wir, ob die aktuelle Konfiguration wirklich unterschiedlich zu der aus dem localStorage ist,
              // um den User nicht zu nerven, falls die autosave-Konfiguration gleich der geladenen ist
              // hier interessiert nur der Json-Teil, weil der Rest reine Meta-Information ist und sich da Felder ändern können, z.B. last_access
              let storedJson = storedConfiguration.Json;
              let currentJson = APP.getStageAsConfiguration().Json;
              if (!Commons.isEqualEpsilon(storedJson, currentJson)) {
                console.log("found an unsaved localStorage.configuration");

                const autoOK = APP.settings.userRole == "admin";
                const restore = () => {
                  this.setStageFromConfiguration(storedConfiguration);
                  if (cameraConfig) {
                    this.setCameraConfig(cameraConfig);
                  }
                  this._setConfigurationCookie();
                  APP.trigger("changedConfiguration");
                  APP.trigger("loadedConfiguration");
                  this._setConfigurationSnapshot();
                };

                const askRestoreQuestion = () =>
                  Dialogs.askQuestion(
                    I18N("Wir haben eine Konfiguration im Speicher gefunden. Möchten"),
                    [
                      { label: I18N("Ja"), primary: true, action: restore },
                      {
                        label: I18N("Nein"),
                        action: deleteStoredConfiguration,
                      },
                    ],
                  );

                const askRestoreOrAutoRestore = () => {
                  if (autoOK) {
                    Dialogs.showToast(
                      I18N("Weil sie als Administrator eingeloggt sind, wurde die"),
                    );
                    _.delay(restore, 300);
                  } else {
                    askRestoreQuestion();
                  }
                };

                const codeParam = Urls.urlParam("Code");

                if (
                  !Urls.urlParam("test") &&
                  Urls.urlParam("autoSave") != "false" &&
                  !_.isEmpty(codeParam) &&
                  storedConfiguration.Code == codeParam
                ) {
                  askRestoreOrAutoRestore();
                } else if (!Urls.urlParam("test") && _.isEmpty(codeParam)) {
                  askRestoreQuestion();
                } else {
                  deleteStoredConfiguration();
                }
              }
            } catch (e) {
              console.warn(e);
            }
          }, 2000);
        });
      } catch (e) {
        console.warn(e);
      }
    }
  }

  createWkfgBackendApi() {
    return new Wkfg2BackendApi(this.settings.wkfgBackendUrl, this.createWkfgTypeConverter());
  }

  createWkfgTypeConverter() {
    return new Wkfg2TypeConverter();
  }

  fotorealisticRender({
    projectName = APP.name,
    filename = "render.glb",
    object3d = null,
    code = null,
    before = null,
    after = null,
    hideknown = true,
    instancesToBeHidden = [],
    renderData = { name: "renderData.json" },
    multipart = 1,
    part = 1,
    useRenderfarm = false,
    blendFilename = "auto.blend",
  }: IRenderOptions = {}) {
    if (!projectName || !object3d || !code) {
      throw new Error("MISSING OR INCORRECT PARAMS FOR FOTOREALISTIC RENDER");
    }
    if (!projectName.includes("_")) {
      throw new Error("THE PROJECTNAME NEEDS TO INCLUDE A _ SYMBOL: e.g.: ocschrank_oc");
    }
    renderData = { hdriUrl: "", cameraCount: 1, ...renderData };
    object3d = _.cloneDeep(object3d);
    return Dialogs.defaultCatchAsWarning(
      new Promise<void>((resolve, reject) => {
        this.prepareO3ForExport(object3d!, hideknown, instancesToBeHidden);
        if (before) before(object3d!);
        console.log(object3d!.scale);
        STAGE.gltfToBlob(
          (blob: Blob) => {
            const renderDataJSON = {
              fileName: renderData.name,
              blob: new Blob([JSON.stringify(renderData)], {
                type: "application/json",
              }),
            };
            this.wkfgBackendApi
              .uploadContent(`Konfiguration/${code}`, renderDataJSON as any) // TODO: Check uploadContent signature
              .then(() => {
                const glbUpload = {
                  fileName: filename,
                  blob: blob,
                };
                this.wkfgBackendApi
                  .fotorealisticRenderAPI(
                    `Konfiguration/${code}`,
                    glbUpload,
                    projectName!,
                    multipart,
                    part,
                    useRenderfarm,
                    blendFilename,
                  )
                  .then(() => {
                    resolve();
                  })
                  .catch((e: any) => reject(e));
              })
              .catch((e: any) => reject(e));
          },
          { binary: true, googleARUVMapping: false },
          object3d,
        );
        if (after) after(object3d!);
      }),
    );
  }

  augmentedReality({
    projectName = APP.name ? APP.name : null,
    ARUvMapping = null,
    convertToUSDZ = null,
    object3d = null,
    code = null,
    before = null,
    after = null,
    hideknown = true,
    instancesToBeHidden = [],
  }: IAROptions = {}) {
    if (!projectName || !object3d || !code) {
      throw new Error("MISSING OR INCORRECT PARAMS FOR FOTOREALISTIC RENDER");
    }
    object3d = _.cloneDeep(object3d);
    return Dialogs.defaultCatchAsWarning(
      new Promise<void>((resolve, reject) => {
        this.getNextArFileIndex(code!).then((index: number) => {
          this.prepareO3ForExport(object3d!, hideknown, instancesToBeHidden);
          const filename = `ar${index}.glb`;
          if (before) before(object3d!);
          STAGE.gltfToBlob(
            (blob: Blob) => {
              const glbUpload = {
                fileName: filename,
                blob: blob,
              };
              this.wkfgBackendApi
                .augmentedRealityAPI(
                  `Konfiguration/${code}`,
                  glbUpload,
                  projectName!,
                  convertToUSDZ!,
                )
                .then(() => {
                  resolve();
                })
                .catch((e: any) => reject(e));
            },
            { binary: true, googleARUVMapping: ARUvMapping },
            object3d,
          );
          if (after) after(object3d!);
        });
      }),
    );
  }

  getNextArFileIndex(code: string): Promise<number> {
    return Dialogs.defaultCatchAsWarning(
      new Promise((resolve, reject) => {
        this.wkfgBackendApi.listContent("Konfiguration/" + code).then((list: string[]) => {
          let names: string[] = [];
          list.forEach((e) => {
            const from = e.lastIndexOf("/") + 1;
            const to = e.lastIndexOf(".");
            names.push(e.slice(from, to));
          });
          names = names.filter((n) => n.includes("ar"));
          resolve(names.length);
        });
      }),
    );
  }

  prepareO3ForExport(
    object3d: Object3D,
    hideknown: boolean = true,
    instancesToBeHidden: any[] = [],
  ) {
    if (hideknown) {
      this.changeVisibilityForClass(LineSegments, false, object3d);
    }
    instancesToBeHidden.forEach((i) => {
      this.changeVisibilityForClass(i, false, object3d);
    });
  }

  changeVisibilityForClass(classname: any, bool: boolean, object3d: Object3D) {
    object3d.traverse((o3: any) => {
      if (o3 instanceof classname) {
        if (!bool && o3.visible) {
          o3.visible = bool;
        } else {
          o3.visible = bool;
        }

        if (o3.material) {
          if (bool && o3.material.visible) {
            o3.material.visible = bool;
          } else {
            o3.material.visible = bool;
          }
        }
      }
    });
  }

  changeVisibilityForInstance(instance: Object3D, bool: boolean) {
    instance.visible = bool;
  }

  isConfigurationChanged() {
    const now = APP._configurationSnapshot;
    const snapshot = APP.getStageAsConfiguration();

    const nowString = JSON.stringify(now);
    const snapshotString = JSON.stringify(snapshot);

    return nowString != snapshotString;
  }

  configurationFotoSnapshots() {
    return [
      {
        name: "Vorderansicht",
        direction: { x: 0, y: 0.2, z: 1 },
        object: { x: 0, y: 0, z: 0 },
        hideObjectsByName: ["ground", "coordinateOffset"],
        zoomTo: true,
        extraZoom: 1,
        backgroundColor: 0xffffff,
        backgroundAlpha: 1,
        renderWidth: 800,
        renderHeight: 496,
        mimeType: "image/jpeg",
      },
      {
        name: "Hinteransicht",
        direction: { x: 0, y: 0.2, z: -1 },
        object: { x: 0, y: 0, z: 0 },
        hideObjectsByName: ["ground", "coordinateOffset"],
        zoomTo: true,
        extraZoom: 1,
        backgroundColor: 0xffffff,
        backgroundAlpha: 1,
        renderWidth: 800,
        renderHeight: 496,
        mimeType: "image/jpeg",
      },
    ];
  }

  get contentUrl() {
    return (this.settings.wkfgContentBaseUrl as any).rtrim("/");
  }

  getContentUrl(columnMeta: any, value: string | null) {
    if (Commons.isEmpty(value)) {
      return null;
    }
    if (columnMeta == null) {
      throw new Error("columnMeta is null");
    }
    if (/^(http|https|file):\/\/.*$/.test(value!)) {
      return value;
    }
    let url = this.contentUrl + "/";
    if (!Commons.isEmpty(columnMeta.contentPath)) {
      const contentPath = (columnMeta.contentPath as any).ensureRight("/");
      if ((value == null || !value.startsWith("/")) && columnMeta.stripContentPath !== false) {
        url += contentPath;
      }
    }
    if (!Commons.isEmpty(value)) {
      url += value;
    }
    if (!Commons.isEmpty(columnMeta.defaultSuffix) && !/.*(\.[a-z0-9]{1,5})$/i.test(url)) {
      url += (columnMeta.defaultSuffix as any).ensureLeft(".");
    }
    url = url.replace(/\s/g, "%20");
    return url;
  }

  getConfigurationStandardImageUrl() {
    return this.getConfigurationContentUrl() + "/Foto.jpg";
  }

  loadMetadata(query: string = "") {
    return this.wkfgBackendApi.loadMetadata(query).then(({ metadata, metadataPlain }) => {
      this.checkMetadataMustBeSet(metadata);
      APP.metadata = metadata;
      APP.trigger("loadedMetadata");
      return APP.metadata;
    });
  }

  loadConfiguration(
    configurationQuery: string,
    successCallback: ((config: any) => void) | undefined = undefined,
    failureCallback: ((error: any) => void) | undefined = undefined,
  ) {
    const notFound = (error: any) => /no record matching/g.test(error.message);

    const defaultFailureCallback = (error: any) => {
      if (notFound(error)) {
        Dialogs.showError(I18N("NothingFoundAtThisAdress_GoBack", APP.settings.homeUrl));
      } else {
        throw error;
      }
    };

    return this.wkfgBackendApi
      .loadConfiguration(configurationQuery)
      .catch(failureCallback || defaultFailureCallback)
      .then((configuration) => {
        console.log("load configuration", _.cloneDeep(configuration));
        if (!configuration) {
          Dialogs.showError(I18N("NothingFoundAtThisAdress_GoBack", APP.settings.homeUrl));
          return;
        }
        if (successCallback) {
          successCallback(configuration);
        }
        this._setConfigurationCookie();
        APP.trigger("changedConfiguration");
        APP.trigger("loadedConfiguration");
        this._setConfigurationSnapshot();
      });
  }

  loadInitialConfiguration() {
    APP.onOnce("ready", () => {
      _.delay(() => {
        this._setConfigurationSnapshot();
      }, 1000);
    });

    const code = _.isEmpty(Urls.urlParam("Code")) ? this.settings.Code : Urls.urlParam("Code");
    const id = Urls.urlParam("id");
    let searchStringStripped = "";
    if (!_.isEmpty(code)) {
      searchStringStripped = "&where.Code=" + code;
    } else if (!_.isEmpty(id)) {
      searchStringStripped = "&where.id=" + id;
    }

    if (searchStringStripped === "") {
      this.setStageFromConfiguration(null);
      this._setConfigurationCookie();
      APP.trigger("changedConfiguration");
      APP.trigger("loadedConfiguration");
      APP.trigger("loadedInitialConfiguration");
      this._setConfigurationSnapshot();
    } else {
      this.loadConfiguration(searchStringStripped, (configuration) => {
        this.setStageFromConfiguration(configuration);
        APP.trigger("loadedInitialConfiguration");
      });
    }
  }

  updatePrices(callback: (result: any) => void) {
    this.wkfgBackendApi
      .updatePrices(APP.getStageAsConfiguration())
      .then((result) => callback(result));
  }

  addOnUnloadWarning() {
    if (APP.settings.userRole == "admin") {
      return;
    }
    self.addEventListener("beforeunload", (event) => {
      if (APP.isConfigurationChanged()) {
        // standard way to trigger confirmation dialog in beforeunload
        event.preventDefault();
        event.returnValue = I18N("YouHaveUnsavedChanges");
        return I18N("YouHaveUnsavedChanges");
      }
    });
  }

  checkMetadataMustBeSet(data: any) {
    if (_.isEmpty(data) || _.every(_.values(data), (x) => _.isEmpty(x))) {
      console.error("Metadata is empty! result from server was: ", data);
    }
  }

  getConfigurationContentUrl() {
    return APP.settings.wkfgContentBaseUrl + "Konfiguration/" + this.getCurrentConfiguration().Code;
  }

  shareFacebook() {
    const myURL = APP.settings.configuratorUrl + "?Code=" + this.getCurrentConfiguration().Code;
    this._shareSocial(
      "Facebook",
      APP.settings.pleaseWaitFacebookUrl,
      "http://www.facebook.com/sharer/sharer.php?u=" +
        encodeURIComponent(myURL) +
        "&t=" +
        I18N("App-Name"),
    );
  }

  sharePinterest() {
    const myURL = APP.settings.configuratorUrl + "?Code=" + this.getCurrentConfiguration().Code;
    const myImg = this.getConfigurationStandardImageUrl();
    this._shareSocial(
      "Pinterest",
      APP.settings.pleaseWaitPinterestUrl,
      "http://pinterest.com/pin/create/button/?url=" +
        encodeURIComponent(myURL) +
        "&media=" +
        encodeURIComponent(myImg) +
        "&description=" +
        I18N("App-Name"),
    );
  }

  pdf(callback: ((config: any) => void) | null = null, options: any = {}) {
    if (this._isValidationErrorAndMaybeShowError(I18N("ausgedruckt"))) {
      return;
    }

    STAGE.setRenderOffline(true);

    const waitingDialog = Dialogs.showWaiting(I18N("das PDF wird erzeugt..."));

    const opts: ISaveOptions = Commons.defaultsDeepSafe({}, options, {
      callback: (configuration: any) => {
        STAGE.setRenderOffline(false);

        const pdfUrlObject = new URL(this.wkfgBackendApi.getPdfUrl(configuration.Code, false));
        Array.from(new URL(document.location.href).searchParams.entries())
          .filter((pair) => /tenant|lang/.test(pair[0]))
          .forEach((pair) => {
            pdfUrlObject.searchParams.set(pair[0], pair[1]);
          });
        if (!Commons.isEmpty(options.additionalParameter)) {
          Object.keys(options.additionalParameter).forEach((k) => {
            pdfUrlObject.searchParams.set(k, options.additionalParameter[k]);
          });
        }
        const pdfUrl = pdfUrlObject.toString();
        console.log("pdf wird erzeugt: ", pdfUrl);

        if (DeviceDetection.isDesktop()) {
          const iframe = Doms.createElementFromHTML(
            '<iframe id="printIFrame" width="100px" height="100px" src="' + pdfUrl + '"/>',
          );
          document.body.appendChild(iframe);
          _.delay(() => {
            this._setConfigurationCookie();
            waitingDialog();
            Dialogs.showToast(I18N("das PDF wurde heruntergeladen"));
            _.delay(() => Doms.removeElement(iframe), 10000);
          }, 2000);
        } else {
          waitingDialog();
          Dialogs.showOk(
            I18N("Sie können das PDF über folgenden Link öffnen: <br/><br/> <a", pdfUrl, pdfUrl),
          );
        }
        if (callback) callback(configuration);
      },
      onCancel: () => {
        waitingDialog();
        STAGE.setRenderOffline(false);
      },
      preferUpdate: true,
      quiet: true,
    });
    if (this.configuration?.Locked && !this.isConfigurationChanged()) {
      if (opts.callback) opts.callback(this.configuration);
    } else {
      this.save(opts);
    }
  }

  save(options: ISaveOptions) {
    const opts: ISaveOptions = Commons.defaultsDeepSafe({}, options, {
      callback: (configuration: any) => {},
      inSaveCallback: (cb: () => void) => cb(),
      preferUpdate: false,
      forceNoUpdate: false,
      quiet: false,
      noFotoSnapshots: false,
      modifyBeforeWrite: (configuration: any, method: string) => {},
      onCancel: () => {},
    });

    const configuration = APP.getStageAsConfiguration();

    if (
      this._isValidationErrorAndMaybeShowError(
        "CantSaveTheConfigurationBecauseOfTheFollowingErrors",
      )
    ) {
      return;
    }

    const create = "create";
    const update = "update";
    const create_or_update = "create_or_update";

    const decision_table: Record<string, Record<string, string>> = {
      admin: {
        default_config: create,
        inspiration_config: create_or_update,
        admin_config: create_or_update,
        customer_config: create_or_update,
        locked_config: create_or_update,
      },
      haendler: {
        default_config: create,
        inspiration_config: create,
        admin_config: create,
        customer_config: create_or_update,
        locked_config: create_or_update,
      },
      kunde_eingeloggt: {
        default_config: create,
        inspiration_config: create,
        admin_config: create,
        customer_config: create_or_update,
        locked_config: create,
      },
      kunde_nicht_eingeloggt: {
        default_config: create,
        inspiration_config: create,
        admin_config: create,
        customer_config: create_or_update,
        locked_config: create,
      },
    };

    const role = APP.settings.userRole;

    const configurationType = this.getConfigurationType(configuration);
    let decision = decision_table[role][configurationType] as
      | "create"
      | "update"
      | "create_or_update";

    if (opts.preferUpdate && decision == create_or_update) {
      decision = update;
    } else if (!opts.preferUpdate && opts.forceNoUpdate) {
      decision = create;
    }

    if (configurationType == "locked_config") {
      if (decision == create) {
        Dialogs.askQuestion(I18N("LockedCreateANewConfiguration"), [
          {
            label: I18N("CreateNew"),
            primary: true,
            action: () => {
              configuration.Locked = false;
              this._saveAction(
                configuration,
                create,
                opts.callback,
                opts.quiet,
                opts.noFotoSnapshots,
                opts.modifyBeforeWrite,
                opts.inSaveCallback,
              );
            },
          },
          {
            label: I18N("Cancel"),
            action: () => {
              if (opts.onCancel) opts.onCancel();
            },
          },
        ]);
      } else {
        Dialogs.askQuestion(I18N("LockedOverrideOrCreateNew"), [
          {
            label: I18N("CreateNew"),
            primary: true,
            action: () => {
              configuration.Locked = false;
              this._saveAction(
                configuration,
                create,
                opts.callback,
                opts.quiet,
                opts.noFotoSnapshots,
                opts.modifyBeforeWrite,
                opts.inSaveCallback,
              );
            },
          },
          {
            label: I18N("Overwrite"),
            action: () =>
              this._saveAction(
                configuration,
                update,
                opts.callback,
                opts.quiet,
                opts.noFotoSnapshots,
                opts.modifyBeforeWrite,
                opts.inSaveCallback,
              ),
          },
          {
            label: I18N("Cancel"),
            action: () => {
              if (opts.onCancel) opts.onCancel();
            },
          },
        ]);
      }
    } else if (decision == create_or_update) {
      Dialogs.askQuestion(I18N("WouldYouLikeToReplaceOrAddTheConfiguration"), [
        {
          label: I18N("Overwrite"),
          primary: true,
          action: () =>
            this._saveAction(
              configuration,
              update,
              opts.callback,
              opts.quiet,
              opts.noFotoSnapshots,
              opts.modifyBeforeWrite,
              opts.inSaveCallback,
            ),
        },
        {
          label: I18N("CreateNew"),
          action: () =>
            this._saveAction(
              configuration,
              create,
              opts.callback,
              opts.quiet,
              opts.noFotoSnapshots,
              opts.modifyBeforeWrite,
              opts.inSaveCallback,
            ),
        },
      ]);
    } else {
      this._saveAction(
        configuration,
        decision,
        opts.callback,
        opts.quiet,
        opts.noFotoSnapshots,
        opts.modifyBeforeWrite,
        opts.inSaveCallback,
      );
    }
  }

  getConfigurationType(configuration: IConfiguration) {
    if (typeof configuration.id === "undefined") {
      return "default_config";
    } else if (configuration.Inspiration) {
      return "inspiration_config";
    } else if (configuration.Locked) {
      return "locked_config";
    } else if (configuration.UserGuid == APP.settings.userGuid) {
      return "customer_config";
    } else {
      return "admin_config";
    }
  }

  loadConfigData(options: any) {
    return this.wkfgBackendApi.loadConfigData(options).then((data) => {
      APP.metamodel = data.metamodel;
      APP.metadata = data.metadata;
      APP.configuration = _.get(data, "Konfiguration[0]");
    });
  }

  loadInitialConfigurationFromApp() {
    APP.trigger("loadedMetamodel");
    const initialConfiguration = APP.configuration;
    this.setStageFromConfiguration(initialConfiguration);
    if (initialConfiguration == null) {
      this._setConfigurationCookie();
      APP.trigger("changedConfiguration");
      APP.trigger("loadedConfiguration");
      APP.trigger("loadedInitialConfiguration");
      this._setConfigurationSnapshot();
    } else {
      APP.trigger("loadedInitialConfiguration");
    }
  }

  loadMetamodel(callback = (metamodell: any) => {}) {
    return this.wkfgBackendApi.loadMetamodel().then((data) => {
      APP.metamodel = data;
      callback(APP.metamodel);
      APP.trigger("loadedMetamodel");
      return APP.metamodel;
    });
  }

  deleteConfiguration(id: string, callback = () => {}) {
    return this.wkfgBackendApi.deleteMetadata("Konfiguration", id).then(callback);
  }

  _saveAction(
    configuration: any,
    method: "create" | "update",
    callback: (config: any) => void = (configuration) => {},
    quiet: boolean = false,
    noFotoSnapshots: boolean = false,
    modifyBeforeWrite: (config: any, method: string) => void = () => {},
    inSaveCallback: (cb: () => void) => void = (cb) => cb(),
  ) {
    // Deep-Clone VOR jeder Mutation, damit das Original-Objekt im App-State nicht verändert wird
    configuration = Commons.cloneDeep(configuration);

    const saveIntern = () => {
      let freeze: any = document.createElement("div");
      let waitingDialog: () => void;

      if (!quiet) {
        waitingDialog = Dialogs.showWaiting(I18N("saving..."));
      }

      if (!noFotoSnapshots) {
        freeze = STAGE.freezeRendererAndBlur();
        STAGE.stopAnimation = true;
        if (APP.representer) {
          APP.representer.disabled = true;
        }
      }
      _.delay(
        () => {
          configuration.UserGuid = APP.settings.userGuid;
          configuration.camera = STAGE.getCurrentCameraConfig();


          this.wkfgBackendApi
            .saveMetadata("Konfiguration", method, configuration)
            .then((configurationFromServer) => {
              const setBrowserUrlWithoutReload = () => {
                const code = configurationFromServer.Code;
                let configuratorUrl = Urls.addOrUpdateQsParameter(
                  window.location.href,
                  "Code",
                  code,
                );
                if (!Commons.isEmpty(code) && !Commons.isEmpty(configuratorUrl)) {
                  history.pushState(null, "", configuratorUrl);
                }
              };

              console.log("configurationFromServer", configurationFromServer);

              this.updateConfigurationWithUnchangedJson(configurationFromServer);

              setBrowserUrlWithoutReload();

              this.uploadSketchesReady = !this.uploadSketches;
              this.uploadSketchesTimeout = new Date().getTime() + 20000;
              if (this.uploadSketches) {
                const sketchDatas = this.uploadSketches()?.filter(
                  (sketchData) => sketchData?.canvasPromise != null,
                );
                const sketchPromises = sketchDatas.map((sketchData) => sketchData.canvasPromise);
                Promise.all(sketchPromises).then((canvases) => {
                  Promise.all(
                    canvases.map(
                      (canvas, index) =>
                        new Promise((resolve) =>
                          canvas.toBlob(
                            resolve as BlobCallback,
                            getMimetypeOfFilename(sketchDatas[index].name),
                          ),
                        ),
                    ),
                  ).then((canvasBlobs) => {
                    const filesArray = canvasBlobs.map((canvasBlob, index) => {
                      return {
                        fileName: sketchDatas[index].name,
                        blob: canvasBlob as Blob,
                      };
                    });
                    this.wkfgBackendApi
                      .uploadContent("Konfiguration/" + configurationFromServer.Code, filesArray)
                      .then(
                        () => {
                          this.uploadSketchesReady = true;
                          console.log("Sketches upload fertig");
                        },
                        () => {
                          this.uploadSketchesReady = true;
                          console.log("Sketches upload fehlgeschlagen", sketchDatas);
                        },
                      );
                  });
                });
              }

              const appendFileNameAndBlobForUpload = (
                options: any,
                fileNameAndBlobsArray: any[],
                uploadCallback: () => void,
              ) => {
                if (options.glb === true) {
                  STAGE.gltfToBlob((blob: Blob) => {
                    fileNameAndBlobsArray.push({
                      fileName: options.name + ".glb",
                      blob: blob,
                    });
                    uploadCallback();
                  });
                } else {
                  STAGE.setRendererToSize(options.renderWidth, options.renderHeight);
                  const undo = STAGE.setCameraAndRenderConfigAndReturnUndo(options);

                  _.delay(
                    () => {
                      STAGE.renderToBlob(options.mimeType, (blob: Blob) => {
                        undo();

                        const suffix = "." + Mimetypes.getSuffixOfMimetype(options.mimeType);

                        fileNameAndBlobsArray.push({
                          fileName: options.name + suffix,
                          blob: blob,
                        });
                        uploadCallback();
                      });
                    },
                    options.timeBeforeRender != null ? options.timeBeforeRender : 1000,
                  );
                }
              };

              if (!noFotoSnapshots) {
                const fileNameAndBlobsArray: any[] = [];
                const snapshots = _.map(_.clone(this.configurationFotoSnapshots()), (x) => {
                  return _.curry(appendFileNameAndBlobForUpload)(x, fileNameAndBlobsArray);
                });

                const callSnaphots = (snapshots: any[]) => {
                  if (snapshots.length > 0) {
                    const fn = _.head(snapshots);
                    fn(_.partial(callSnaphots, _.slice(snapshots, 1)));
                  } else {
                    this.wkfgBackendApi.uploadContent(
                      "Konfiguration/" + configurationFromServer.Code,
                      fileNameAndBlobsArray,
                    );
                    STAGE.setRendererToSize();
                    STAGE.stopAnimation = false;
                    if (APP.representer) {
                      APP.representer.disabled = false;
                    }
                    APP.trigger("savedConfiguration");
                    this._setConfigurationCookie();
                    this._setConfigurationSnapshot();
                    const waitingTimeForServerProcessing =
                      1000 + fileNameAndBlobsArray.length * 300;
                    _.delay(() => {
                      if (!quiet) {
                        APP.showSavedDialog(configurationFromServer);
                      }
                      inSaveCallback(() => {
                        waitingDialog();
                        Doms.hide(freeze.hide);
                        callback(configurationFromServer);
                      });
                    }, waitingTimeForServerProcessing);
                  }
                };
                callSnaphots(snapshots as any[]);
              } else {
                Commons.delayUntil(
                  () => {
                    APP.trigger("savedConfiguration");
                    this._setConfigurationCookie();
                    this._setConfigurationSnapshot();
                    if (!quiet) {
                      APP.showSavedDialog(configurationFromServer);
                      waitingDialog();
                    }
                    inSaveCallback(() => {
                      callback(configurationFromServer);
                    });
                  },
                  () =>
                    this.uploadSketchesReady || new Date().getTime() > this.uploadSketchesTimeout,
                );
              }
            });
        },
        noFotoSnapshots ? 0 : 500,
      );
    };

    configuration.UserId = APP.settings.userId;

    if (method == "create") {
      configuration.id = null;
      configuration.Code = null;
    }

    if (APP.settings.userRole != "admin") {
      configuration.Inspiration = false;
    }

    modifyBeforeWrite(configuration, method);
    APP.trigger("beforeSaveConfiguration", configuration, method);

    saveIntern();
  }

  _isValidationErrorAndMaybeShowError(errorMessage: string) {
    const validationErrors = this.getStageValidationErrors();
    if (_.isEmpty(validationErrors)) {
      return false;
    }
    if (I18N) {
      errorMessage = I18N(errorMessage);
    }
    Dialogs.showWarning(errorMessage + " <br/> " + validationErrors.join("<br/>"));
    return true;
  }

  _shareSocial(socialNetworkName: string, pleaseWaitUrl: string, shareUrl: string) {
    const width = 800;
    const height = 600;
    const win = window.open(
      pleaseWaitUrl,
      socialNetworkName,
      "toolbar=0, status=0, width=" + width + ", height=" + height,
    );
    this.save({
      preferUpdate: true,
      quiet: true,
      callback: function () {
        if (win) win.location.href = shareUrl;
      },
    });
  }

  _setConfigurationCookie() {
    const configuration = this.getCurrentConfiguration();
    if (configuration?.Code) {
      Cookies.set("holodeck_configuration_code_cookie", this.getCurrentConfiguration().Code!);
    }
  }

  _setConfigurationSnapshot() {
    APP._configurationSnapshot = Commons.cloneDeep(this.getStageAsConfiguration());
  }

  get wkfgBackendApi(): Wkfg2BackendApi {
    if (this._wkfgBackendApi == null) {
      this._wkfgBackendApi = new Wkfg2BackendApi((this.settings as any).wkfgBackendUrl);
    }
    return this._wkfgBackendApi;
  }

  set wkfgBackendApi(value: Wkfg2BackendApi) {
    this._wkfgBackendApi = value;
  }

  get modelsHelper(): ModelsHelper {
    if (this._modelsHelper == null) {
      this._modelsHelper = new ModelsHelper();
    }
    return this._modelsHelper;
  }

  set modelsHelper(value: ModelsHelper) {
    this._modelsHelper = value;
  }
}
